import { useEffect, useState } from "react";
import type {
  CoachingClip,
  CoachingNote,
  CoachingPlaylist,
  FeedbackBundle,
  PlayerGoal,
} from "@/lib/types";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import { PlayerDevelopmentProfile } from "./PlayerDevelopmentProfile";
import { GoalList } from "./components/GoalList";
import { GoalReflectionModal } from "./components/GoalReflectionModal";
import { useMyPlayerDevelopment } from "./hooks/useDevelopmentProfile";

/**
 * `/feedback` Development sub-tab (M7) — the viewer side of the
 * shared Player Development painter. Ports legacy
 * `renderFeedbackDevelopment` (`js/coaching/development.js`).
 *
 * Sticky player selection: the active player id persists across
 * re-renders (so a re-fetch doesn't yank the viewer back to the
 * first player). When the account is linked to multiple players
 * a chip selector switches profiles; with one linked player it
 * renders the profile straight away.
 *
 * The painter is presentational — this host wires the
 * recent-note / clip / playlist rows to open the M5
 * `<FeedbackPlayerModal>`. A recent playlist row is the
 * lightweight `DevelopmentPlaylistRow`; the modal needs the full
 * `CoachingPlaylist`, so we resolve it from the `/feedback`
 * bundle by id.
 *
 * Privacy: `useMyPlayerDevelopment` hits the viewer-scoped
 * endpoint — it 404s (→ `null`) for an unrelated player, and the
 * payload omits the coach-only `themes` + `linked_accounts`.
 */

export type FeedbackDevelopmentTabProps = {
  /** The `/feedback` bundle — `players` drives the selector,
   * `playlists` lets us resolve a recent-playlist row to its full
   * object for the modal. */
  bundle: FeedbackBundle | undefined;
  /** Threaded down to the player modal so it can show the
   * Reviewed ✓ chip + flip the Mark reviewed button. */
  reviewedNoteIds?: Set<number>;
  reviewedPlaylistIds?: Set<number>;
};

export function FeedbackDevelopmentTab({
  bundle,
  reviewedNoteIds,
  reviewedPlaylistIds,
}: FeedbackDevelopmentTabProps) {
  const players = bundle?.players ?? [];

  // Sticky active-player id. Seeds to the first linked player;
  // re-seeds only if the current id falls out of the linked set
  // (e.g. a coach unlinked the account mid-session).
  const [activePlayerId, setActivePlayerId] = useState<string | null>(null);
  useEffect(() => {
    if (players.length === 0) {
      setActivePlayerId(null);
      return;
    }
    setActivePlayerId((prev) =>
      prev && players.some((p) => p.id === prev) ? prev : players[0].id,
    );
  }, [players]);

  const devQuery = useMyPlayerDevelopment(activePlayerId);

  // M5-modal wiring.
  const [openNote, setOpenNote] = useState<CoachingNote | null>(null);
  const [openClip, setOpenClip] = useState<CoachingClip | null>(null);
  const [openPlaylist, setOpenPlaylist] = useState<CoachingPlaylist | null>(
    null,
  );
  // The goal whose reflection modal is open, or null.
  const [reflectGoal, setReflectGoal] = useState<PlayerGoal | null>(null);

  if (players.length === 0) {
    return (
      <p
        className="text-ink-muted text-sm px-3 py-6"
        data-testid="feedback-development-no-players"
      >
        No roster player is linked to your account yet. Ask a coach to
        link you.
      </p>
    );
  }

  return (
    <div data-testid="feedback-development-tab">
      {/* Player selector — only when more than one is linked. */}
      {players.length > 1 ? (
        <div
          className="flex flex-wrap gap-1.5 mb-4"
          role="tablist"
          aria-label="Linked player"
          data-testid="feedback-development-player-selector"
        >
          {players.map((p) => {
            const active = p.id === activePlayerId;
            return (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActivePlayerId(p.id)}
                data-feedback-dev-player={p.id}
                data-active={active}
                className={[
                  "rounded px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors",
                  active
                    ? "bg-accent text-white"
                    : "bg-surface text-ink-muted hover:text-ink",
                ].join(" ")}
              >
                {p.jersey_number
                  ? `#${p.jersey_number} ${p.display_name}`
                  : p.display_name}
              </button>
            );
          })}
        </div>
      ) : null}

      {devQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="feedback-development-loading"
        >
          Loading development profile…
        </p>
      ) : devQuery.isError ? (
        <p
          className="text-destructive font-mono text-sm px-3 py-6"
          data-testid="feedback-development-error"
        >
          Could not load the development profile.
        </p>
      ) : devQuery.data == null ? (
        // The verb collapses a 404 (unknown OR unrelated player) to
        // `null` — surface ONE "not available" message, never a
        // distinct "no such player".
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="feedback-development-unavailable"
        >
          Development profile not available.
        </p>
      ) : (
        <PlayerDevelopmentProfile
          profile={devQuery.data}
          viewer
          goalsSlot={
            <GoalList
              goals={devQuery.data.active_goals}
              viewer
              onAddReflection={setReflectGoal}
            />
          }
          onOpenNote={setOpenNote}
          onOpenClip={setOpenClip}
          onOpenPlaylist={(row) => {
            // Resolve the lightweight dev-profile row to the full
            // playlist object the M5 modal needs. If it isn't in
            // the bundle (shouldn't happen — a recent playlist the
            // viewer sees is in their bundle), the click no-ops.
            const full = (bundle?.playlists ?? []).find(
              (p) => p.id === row.id,
            );
            if (full) setOpenPlaylist(full);
          }}
        />
      )}

      {/* M5 focused player — note / clip / playlist modes. */}
      {openNote ? (
        <FeedbackPlayerModal
          target={{ kind: "note", note: openNote }}
          match={{
            id: openNote.match_id ?? "",
            team_id: openNote.team_id,
          }}
          onClose={() => setOpenNote(null)}
          players={players}
          reviewedNoteIds={reviewedNoteIds}
        />
      ) : null}
      {openClip ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: openClip }}
          match={{ id: openClip.match_id, team_id: openClip.team_id }}
          onClose={() => setOpenClip(null)}
          players={players}
        />
      ) : null}
      {openPlaylist ? (
        <FeedbackPlayerModal
          target={{ kind: "playlist", playlist: openPlaylist }}
          match={{
            id: openPlaylist.items?.[0]?.match_id ?? "",
            team_id: openPlaylist.items?.[0]?.team_id ?? openPlaylist.team_id,
          }}
          onClose={() => setOpenPlaylist(null)}
          players={players}
          reviewedNoteIds={reviewedNoteIds}
          reviewedPlaylistIds={reviewedPlaylistIds}
        />
      ) : null}

      {/* Viewer goal-reflection modal. */}
      {reflectGoal ? (
        <GoalReflectionModal
          goal={reflectGoal}
          onClose={() => setReflectGoal(null)}
        />
      ) : null}
    </div>
  );
}
